import * as THREE from 'three';
import { create } from 'zustand';
import type { ProceduralFacilitySpec } from '../types';
import { Request, type Credit } from 'cesium';
import { roofAtlas, roofUv } from './roofImageryMath';
import { roofTextures, roofTextureAtlases, roofTextureSources } from './roofTexture';
import { siteImagery, setRoofCredits } from './siteImagery';

type RoofStatus = 'loading' | 'ready' | 'partial' | 'unavailable';
export const useRoofImagery = create<{entries: Record<string, RoofStatus>}>(() => ({entries: {}}));
const status = (id: string, value: RoofStatus) => useRoofImagery.setState(s => ({entries: {...s.entries, [id]: value}}));

/** Cesium decodes ImageBitmaps with flipY=true for its WebGL textures. Canvas
 * atlases need north-up pixels; HTML image fallback is already north-up. */
export function paintRoofTile(context: CanvasRenderingContext2D, image: CanvasImageSource, col: number, row: number, size: number) {
  const flipped = typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap;
  context.save();
  context.translate(col * size, (row + (flipped ? 1 : 0)) * size);
  if (flipped) context.scale(1, -1);
  context.drawImage(image, 0, 0, size, size);
  context.restore();
}

/** Project the same georeferenced aerial imagery onto roof caps only.
 * Texture detail is photographic evidence, not reconstructed roof equipment or interiors.
 */
export function applyRoofImagery(root: THREE.Object3D, spec: ProceduralFacilitySpec, invalidate: () => void): () => void {
  let active = true;
  const textures = new Set<THREE.Texture>();
  const roofs: THREE.Mesh[] = [];
  const requests = new Set<Request>();
  const creditOwner = Symbol(spec.facilityId);
  const credits = new Map<string, Credit>();
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.userData.aerialRoof) return;
    const texture = roofTextures.get(mesh);
    if (!texture || !roofTextureSources.has(texture)) return;
    textures.add(texture);
    roofs.push(mesh);
  });
  status(spec.facilityId, 'loading');
  void (async () => {
    const {provider} = await siteImagery();
    if (!active) return;
    if (provider.credit) {
      credits.set(provider.credit.html, provider.credit);
      setRoofCredits(creditOwner, [...credits.values()]);
    }
    const tileSize = provider.tileWidth;
    const jobs: {context: CanvasRenderingContext2D; texture: THREE.Texture; x: number; y: number; level: number; col: number; row: number}[] = [];
    for (const texture of textures) {
      const source = roofTextureSources.get(texture)!;
      const atlas = roofAtlas(source.coordinates, source.maxTiles, provider.maximumLevel ?? 21, tileSize);
      if (!atlas) continue;
      roofTextureAtlases.set(texture, atlas);
      const canvas = document.createElement('canvas');
      canvas.width = atlas.cols * tileSize; canvas.height = atlas.rows * tileSize;
      const context = canvas.getContext('2d');
      if (!context) continue;
      context.fillStyle = '#657276'; context.fillRect(0, 0, canvas.width, canvas.height);
      texture.image = canvas; texture.needsUpdate = true;
      for (let row = 0; row < atlas.rows; row++) for (let col = 0; col < atlas.cols; col++) {
        jobs.push({context, texture, col, row, x: atlas.x0 + col, y: atlas.y0 + row, level: atlas.level});
      }
    }
    for (const mesh of roofs) {
      const atlas = roofTextureAtlases.get(roofTextures.get(mesh)!)!;
      const position = mesh.geometry.getAttribute('position'), uv = mesh.geometry.getAttribute('uv');
      for (let i = 0; i < position.count; i++) uv.setXY(i, ...roofUv(position.getX(i), position.getZ(i), spec, atlas));
      uv.needsUpdate = true;
    }
    // Finish the inspected building before the lower-detail campus backdrop.
    jobs.sort((a, b) => b.level - a.level);
    if (!jobs.length) { status(spec.facilityId, 'unavailable'); return; }
    invalidate();
    let index = 0, successes = 0, lastUpload = 0;
    const changed = new Set<THREE.Texture>();
    const upload = () => {
      changed.forEach(texture => {texture.needsUpdate = true;}); changed.clear();
      lastUpload = performance.now(); invalidate();
    };
    const worker = async () => {
      while (active && index < jobs.length) {
        const {context, texture, col, row, x, y, level} = jobs[index++];
        const request = new Request({throttle: false, throttleByServer: false});
        requests.add(request);
        let timer: ReturnType<typeof setTimeout> | undefined;
        let expired = false;
        try {
          const imageRequest = provider.requestImage(x, y, level, request);
          if (!imageRequest) throw new Error('Roof image request not scheduled');
          const decoded = Promise.resolve(imageRequest).then(image => {
            if (expired) (image as {close?: () => void}).close?.();
            return image;
          });
          const image = await Promise.race([decoded, new Promise<never>((_, reject) => {
            timer = setTimeout(() => {expired = true; request.cancelFunction?.(); reject(new Error('Roof image timeout'));}, 15000);
          })]);
          if (active) {
            paintRoofTile(context, image as CanvasImageSource, col, row, tileSize);
            const before = credits.size;
            for (const credit of provider.getTileCredits(x, y, level) ?? []) credits.set(credit.html, credit);
            if (credits.size !== before) setRoofCredits(creditOwner, [...credits.values()]);
            changed.add(texture); successes++;
            // A focused atlas can be several megabytes. Batch arrivals instead
            // of uploading the complete texture for every individual tile.
            if (performance.now() - lastUpload >= 150) upload();
          }
          // The source owns image decoding; retain only this view's GPU atlas.
          (image as {close?: () => void}).close?.();
        } catch { /* Keep the explicit untextured patch; never synthesize roof equipment. */ }
        finally { clearTimeout(timer); requests.delete(request); }
      }
    };
    await Promise.all(Array.from({length: 4}, worker));
    if (!active) return;
    if (successes) {
      upload();
    }
    status(spec.facilityId, successes === jobs.length ? 'ready' : successes ? 'partial' : 'unavailable');
  })().catch(() => { if (active) status(spec.facilityId, 'unavailable'); });
  return () => {
    active = false;
    requests.forEach(request => request.cancelFunction?.());
    setRoofCredits(creditOwner, []);
    textures.forEach(texture => texture.dispose());
  };
}
